import { useState } from "react";

import styles from "./CensusDrawer.module.css";
import { Formik, Form } from "formik";
import { SystemIcon, Button } from "@justworkshr/milo-components";
import { getProspectUuid } from "utils/idHelpers";
import { ConvertToISODate } from "utils/helper";
import {
  GenderTypeEnum,
  ParticipantTypeEnum,
  EmployeeCensusSourceEnum,
  CobraCensusInput,
  useCreateDependentCensusesMutation,
  useUpdateDependentCensusesMutation,
  useUpsertHealthInsEmployeeCensusMutation,
  useUpsertCobraParticipantsMutation,
} from "types/generated/operations";
import Tooltip from "uicomponents/Tooltip";
import Alert from "uicomponents/Alert";
import TextInput from "uicomponents/TextInput";
import RadioInput from "uicomponents/RadioInput";
import DateInput from "uicomponents/DateInput";
import SelectInput from "uicomponents/SelectInput";
import { DependentType, EmployeeType } from "uicomponents/CensusTable/types";
import { stateOption } from "pages/WorkersCompApplcation/utils/selectInputOptions";
import {
  employeeCensusValidationSchema,
  dependentCensusValidationSchema,
  cobraValidationSchema,
  cobraDependentValidationSchema,
} from "./utils/validationSchema";
import { Label } from "@justworkshr/milo-form";
import { hasQueryParam } from "utils/helper";

const stateCodeOptions = stateOption.map((state) => ({
  value: state.description,
  description: state.description,
}));

interface CensusDrawerProps {
  isOpen: boolean;
  closeDrawer: (refetch?: boolean) => void;
  initialData?: DependentType | EmployeeType;
  isCobra?: boolean;
}

const CensusDrawer = ({
  initialData,
  isOpen,
  closeDrawer,
  isCobra,
}: CensusDrawerProps) => {
  const [createDependentCensuses, { loading: createDependentCensusesLoading }] =
    useCreateDependentCensusesMutation();

  const [updateDependentCensuses, { loading: updateDependentCensusesLoading }] =
    useUpdateDependentCensusesMutation();

  const [upsertCobraCensuses, { loading: upsertCobraCensusesLoading }] =
    useUpsertCobraParticipantsMutation();

  const [hasError, setError] = useState(false);

  const [
    upsertHealthEmployeeCensus,
    { loading: upsertHealthEmployeeCensusLoading },
  ] = useUpsertHealthInsEmployeeCensusMutation();

  const anyLoading =
    createDependentCensusesLoading ||
    updateDependentCensusesLoading ||
    upsertHealthEmployeeCensusLoading ||
    upsertCobraCensusesLoading;

  const openClass = isOpen ? styles.open : "";
  const isDependent = Boolean(initialData?.employeeCensusId);

  const v2_dedupe_census_flow = hasQueryParam("usecensustab") && !isCobra;

  let formattedDate = initialData?.dateOfBirth || "";
  if (formattedDate) {
    const dateSubstrings = formattedDate.split("-");
    formattedDate = `${dateSubstrings[1]}/${dateSubstrings[2]}/${dateSubstrings[0]}`;
  }
  let formatedGender = initialData?.gender?.toLowerCase() || "";
  if (formatedGender) {
    formatedGender =
      formatedGender.charAt(0).toUpperCase() + formatedGender.slice(1);
  }

  let defaultIsWaiving = initialData?.healthInsInfo?.isWaiving;
  if (defaultIsWaiving !== undefined && defaultIsWaiving !== null) {
    defaultIsWaiving = defaultIsWaiving ? "Yes" : "No";
  }

  let defaultEmployeeType: any = initialData?.isCompanyOwner;

  if (defaultEmployeeType !== undefined && defaultEmployeeType !== null) {
    defaultEmployeeType = defaultEmployeeType ? "owner" : "employee";
  }

  const defaults = {
    firstName: initialData?.firstName || "",
    lastName: initialData?.lastName || "",
    participantType: initialData?.participantType || "",
    employeeType: defaultEmployeeType || "",
    isWaiving: defaultIsWaiving || "",
    homeZip: initialData?.homeZip || "",
    state: initialData?.homeState || "",
    dateOfBirth: formattedDate,
    gender: formatedGender,
  };

  const handleCobraSubmission = async (values: any) => {
    const formattedCobra: CobraCensusInput = {
      firstName: values.firstName,
      lastName: values.lastName,
      zip: values.homeZip,
      dateOfBirth: ConvertToISODate(values.dateOfBirth),
      gender: values.gender.toUpperCase().replace(/-/g, "_") as GenderTypeEnum,
      participantType: ParticipantTypeEnum.Employee,
    };

    if (initialData?.uuid) {
      formattedCobra.uuid = initialData.uuid;
    }

    if (initialData?.employeeCensusId) {
      formattedCobra.cobraEmployeeId = initialData.employeeCensusId;
      formattedCobra.participantType = values.participantType
        ?.toUpperCase()
        ?.replace(/-/g, "_") as ParticipantTypeEnum;
    }

    try {
      await upsertCobraCensuses({
        variables: {
          prospectId: getProspectUuid(),
          cobraCensuses: [formattedCobra],
        },
      });
      closeDrawer(true);
    } catch {
      setError(true);
    }
  };

  const handleSubmission = async (values: any) => {
    if (isCobra) {
      handleCobraSubmission(values);
      return;
    }

    try {
      if (isDependent) {
        const formattedDependent = {
          employeeId: initialData?.employeeCensusId,
          gender: values.gender
            .toUpperCase()
            .replace(/-/g, "_") as GenderTypeEnum,
          firstName: values.firstName,
          lastName: values.lastName,
          zip: values.homeZip,
          state: values.state || null,
          participantType: values.participantType
            .toUpperCase()
            .replace(/-/g, "_") as ParticipantTypeEnum,
          dateOfBirth: ConvertToISODate(values.dateOfBirth),
        };
        if (!initialData?.uuid) {
          await createDependentCensuses({
            variables: {
              prospectId: getProspectUuid(),
              dependentCensuses: [formattedDependent],
            },
          });
        } else {
          await updateDependentCensuses({
            variables: {
              prospectId: getProspectUuid(),
              dependentCensuses: [
                { ...formattedDependent, uuid: initialData.uuid },
              ],
            },
          });
        }
      } else {
        const formattedEmployee = {
          gender: values.gender
            .toUpperCase()
            .replace(/-/g, "_") as GenderTypeEnum,
          firstName: values.firstName,
          lastName: values.lastName,
          zip: values.homeZip,
          state: values.state || null,
          participantType: ParticipantTypeEnum.Employee,
          dateOfBirth: ConvertToISODate(values.dateOfBirth),
          isCompanyOwner: values.employeeType === "owner" ? true : false,
          isWaiving: values.isWaiving === "Yes" ? true : false,
        };

        // @ts-ignore
        if (initialData?.uuid) formattedEmployee.uuid = initialData.uuid;

        await upsertHealthEmployeeCensus({
          variables: {
            prospectId: getProspectUuid(),
            employeeHealthCensus: formattedEmployee,
            source: EmployeeCensusSourceEnum.WwHiApp,
          },
        });
      }
      closeDrawer(true);
    } catch {
      setError(true);
    }
  };

  let validationSchema: any = employeeCensusValidationSchema;

  if (isCobra) {
    validationSchema = cobraValidationSchema;
  }

  if (isDependent) {
    validationSchema = isCobra
      ? cobraDependentValidationSchema
      : dependentCensusValidationSchema;
  }

  const scrollToError = (fieldName: string) => {
    const element = document.querySelector(`[name="${fieldName}"]`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <div className={`${styles.drawerWrapper} ${openClass}`}>
      <div className={styles.drawerContent}>
        <div className={styles.drawerHeader}>
          <h1>
            {initialData?.firstName ? "Edit" : "Add"}{" "}
            {isDependent ? "dependent" : "teammate"}
          </h1>
          <button
            className={styles.closeButton}
            onClick={() => closeDrawer()}
            aria-label="Close drawer"
            aria-roledescription="button"
          >
            <SystemIcon iconName="close" />
          </button>
        </div>
        <Formik
          initialValues={defaults}
          onSubmit={handleSubmission}
          validationSchema={validationSchema}
        >
          {({ errors, handleSubmit }) => (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                const firstErrorFieldName = Object.keys(errors)[0];
                scrollToError(firstErrorFieldName);
                handleSubmit(e);
              }}
            >
              <div className={styles.drawerBody}>
                <TextInput
                  label="Legal first name"
                  className={styles.drawerInput}
                  name="firstName"
                  disabled={v2_dedupe_census_flow && !isDependent}
                />
                <TextInput
                  label="Legal last name"
                  className={styles.drawerInput}
                  name="lastName"
                  disabled={v2_dedupe_census_flow && !isDependent}
                />
                {isDependent && (
                  <>
                    <SelectInput
                      label="Type"
                      name="participantType"
                      placeholder="Select..."
                      options={[
                        {
                          value: isCobra ? "Spouse" : "spouse",
                          description: "Spouse",
                        },
                        {
                          value: isCobra ? "Child" : "child",
                          description: "Child",
                        },
                      ]}
                    />
                  </>
                )}
                {!isDependent && !isCobra && (
                  <>
                    <SelectInput
                      label="Employee type"
                      name="employeeType"
                      placeholder="Select..."
                      disabled={v2_dedupe_census_flow}
                      options={[
                        {
                          value: "owner",
                          description: "Owner",
                        },
                        {
                          value: "employee",
                          description: "Employee",
                        },
                      ]}
                    />
                    <RadioInput
                      name="isWaiving"
                      options={["Yes", "No"]}
                      label="Are they planning on waiving health insurance coverage?"
                    />
                  </>
                )}
                <DateInput label="Date of birth" name="dateOfBirth" />
                <div className={styles.sexRadioGroup}>
                  <Tooltip tooltipText="Carriers limit us to these options, but we’re working to better reflect everyone.">
                    <Label className={styles.sexLabel}>
                      Sex
                      <span className={styles.sexInfoWrapper}>
                        <SystemIcon
                          iconName="info-circle"
                          size="extra-small"
                          className={styles.sexInfoIcon}
                        />
                      </span>
                    </Label>
                  </Tooltip>
                  <RadioInput
                    name="gender"
                    options={["Male", "Female"]}
                    label="Sex"
                    message="This refers to the sex on most legal documents. The carriers need it
                    to provide the most accurate benefits. It cannot be seen by anyone
                    at your company."
                  />
                </div>
                <div className={styles.drawerInputPair}>
                  <TextInput
                    label="Zip"
                    className={styles.drawerInput}
                    name="homeZip"
                  />
                  {!isCobra && (
                    <SelectInput
                      name="state"
                      options={stateCodeOptions}
                      label={"State"}
                      placeholder="Select..."
                    />
                  )}
                </div>
                {hasError && (
                  <Alert message="Sorry, there was an error. Please try refreshing the page." />
                )}
                <Button
                  className={styles.saveEmployeeButton}
                  color="neutral"
                  loading={anyLoading}
                  type="submit"
                >
                  Save
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CensusDrawer;
